import { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  createFieldConfig,
  updateFieldConfig,
} from "../../../redux/features/fieldConfigs/fieldConfigSlice";

import "./FieldConfigForm.css";

/**
 * =====================================================
 * FIELD TYPES
 * =====================================================
 */

const FIELD_TYPES = [
  {
    value: "text",
    label: "Korte tekst",
  },

  {
    value: "textarea",
    label: "Lange tekst",
  },

  {
    value: "email",
    label: "E-mailadres",
  },

  {
    value: "number",
    label: "Nummer",
  },

  {
    value: "date",
    label: "Datum",
  },

  {
    value: "select",
    label: "Keuzelijst",
  },

  {
    value: "multiselect",
    label: "Meerdere keuzes",
  },

  {
    value: "checkbox",
    label: "Checkbox",
  },

  {
    value: "radio",
    label: "Eén keuze",
  },

  {
    value: "boolean",
    label: "Ja / Nee",
  },

  {
    value: "file",
    label: "Bestand uploaden",
  },
];

/**
 * =====================================================
 * STEP OPTIONS
 * =====================================================
 */

const STEP_OPTIONS = [
  {
    value: "aanmelding",
    label: "Aanmelding",
  },

  {
    value: "administratie",
    label: "Administratie",
  },

  {
    value: "begeleiding",
    label: "Begeleiding",
  },

  {
    value: "vdab",
    label: "VDAB",
  },

  {
    value: "kld",
    label: "KLD",
  },

  {
    value: "afronding",
    label: "Afronding",
  },
];

/**
 * =====================================================
 * WIDTH OPTIONS
 * =====================================================
 */

const WIDTH_OPTIONS = [
  {
    label: "Volledige breedte",
    value: "full",
  },

  {
    label: "Halve breedte",
    value: "half",
  },

  {
    label: "1/3 breedte",
    value: "third",
  },
];

/**
 * =====================================================
 * ROLES
 * =====================================================
 */

const ROLE_OPTIONS = [
  {
    value: "POWER_ADMIN",
    label: "POWER Admin",
  },

  {
    value: "POWER_INTERNAL_COACH",
    label: "Interne coach",
  },

  {
    value: "POWER_EXTERNAL_COACH",
    label: "Externe coach",
  },
];

/**
 * =====================================================
 * HELPERS
 * =====================================================
 */

const TYPES_WITH_OPTIONS = ["select", "radio", "multiselect"];

const TYPES_WITH_MIN_MAX = ["number"];

const TYPES_WITH_TEXT_VALIDATION = ["text", "textarea", "email"];

const createEmptyOption = () => ({
  label: "",
  value: "",
});

/**
 * =====================================================
 * INITIAL STATE
 * =====================================================
 */

const initialFormState = {
  key: "",

  label: "",

  description: "",

  placeholder: "",

  type: "text",

  step: "administratie",

  group: "general",

  width: "full",

  required: false,

  is_readonly: false,

  is_active: true,

  is_system: false,

  order: 0,

  options: [],

  visible_for: [],

  editable_by: [],

  excel_column: "",

  excel_header: "",

  validation: {
    min: "",

    max: "",

    regex: "",

    min_length: "",

    max_length: "",
  },
};

export default function FieldConfigForm({
  mode = "create",
  initialData = null,
  schoolYearId,
  onSuccess,
  onCancel,
}) {
  const dispatch = useDispatch();

  const { isCreating, isUpdating, error } = useSelector(
    (state) => state.fieldConfigs,
  );

  /**
   * =====================================================
   * STATE
   * =====================================================
   */

  const [formData, setFormData] = useState(initialFormState);

  const [validationErrors, setValidationErrors] = useState({});
  /**
   * =====================================================
   * FLAGS
   * =====================================================
   */

  const isEditMode = mode === "edit";

  const isSubmitting = isCreating || isUpdating;

  const needsOptions = useMemo(() => {
    return TYPES_WITH_OPTIONS.includes(formData.type);
  }, [formData.type]);

  const supportsMinMax = useMemo(() => {
    return TYPES_WITH_MIN_MAX.includes(formData.type);
  }, [formData.type]);

  const supportsTextValidation = useMemo(() => {
    return TYPES_WITH_TEXT_VALIDATION.includes(formData.type);
  }, [formData.type]);

  /**
   * =====================================================
   * LOAD EDIT DATA
   * =====================================================
   */

  useEffect(() => {
    if (isEditMode && initialData) {
      setFormData({
        ...initialFormState,

        ...initialData,

        validation: {
          ...initialFormState.validation,

          ...(initialData.validation || {}),
        },

        options: Array.isArray(initialData.options) ? initialData.options : [],

        visible_for: Array.isArray(initialData.visible_for)
          ? initialData.visible_for
          : [],

        editable_by: Array.isArray(initialData.editable_by)
          ? initialData.editable_by
          : [],
      });
    }

    if (!isEditMode) {
      setFormData(initialFormState);
    }
  }, [isEditMode, initialData]);

  /**
   * =====================================================
   * SET FIELD VALUE
   * =====================================================
   */

  const setFieldValue = (name, value) => {
    setFormData((prev) => ({
      ...prev,

      [name]: value,
    }));

    setValidationErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  /**
   * =====================================================
   * INPUT CHANGE
   * =====================================================
   */

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFieldValue(name, type === "checkbox" ? checked : value);
  };

  /**
   * =====================================================
   * TYPE CHANGE
   * =====================================================
   */

  const handleTypeChange = (event) => {
    const nextType = event.target.value;

    setFormData((prev) => ({
      ...prev,

      type: nextType,

      options: TYPES_WITH_OPTIONS.includes(nextType)
        ? prev.options.length
          ? prev.options
          : [createEmptyOption()]
        : [],
    }));
  };

  /**
   * =====================================================
   * VALIDATION CHANGE
   * =====================================================
   */

  const handleValidationChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,

      validation: {
        ...prev.validation,

        [name]: value,
      },
    }));
  };

  /**
   * =====================================================
   * ROLE TOGGLE
   * =====================================================
   */

  const toggleRole = (field, role) => {
    setFormData((prev) => {
      const exists = prev[field].includes(role);

      return {
        ...prev,

        [field]: exists
          ? prev[field].filter((item) => item !== role)
          : [...prev[field], role],
      };
    });
  };

  /**
   * =====================================================
   * OPTIONS
   * =====================================================
   */

  const handleOptionChange = (index, fieldName, value) => {
    setFormData((prev) => {
      const nextOptions = [...prev.options];

      nextOptions[index] = {
        ...nextOptions[index],

        [fieldName]: value,
      };

      return {
        ...prev,

        options: nextOptions,
      };
    });
  };

  const addOption = () => {
    setFormData((prev) => ({
      ...prev,

      options: [...prev.options, createEmptyOption()],
    }));
  };

  const removeOption = (index) => {
    setFormData((prev) => ({
      ...prev,

      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  /**
   * =====================================================
   * NORMALIZE KEY
   * =====================================================
   */

  const normalizeKey = () => {
    const normalized = formData.key
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");

    setFieldValue("key", normalized);
  };

  /**
   * =====================================================
   * VALIDATE
   * =====================================================
   */

  const validateForm = () => {
    const errors = {};

    if (!formData.key.trim()) {
      errors.key = "Key is verplicht.";
    }

    if (!formData.label.trim()) {
      errors.label = "Label is verplicht.";
    }

    if (!formData.step.trim()) {
      errors.step = "Workflow stap is verplicht.";
    }

    if (
      formData.excel_column &&
      !/^[A-Z]+$/.test(formData.excel_column.toUpperCase())
    ) {
      errors.excel_column = "Gebruik enkel geldige Excel kolommen.";
    }

    if (needsOptions && !formData.options.length) {
      errors.options = "Minstens één optie is verplicht.";
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  /**
   * =====================================================
   * BUILD PAYLOAD
   * =====================================================
   */

  const buildPayload = () => {
    return {
      school_year: schoolYearId || initialData?.school_year,

      key: formData.key.trim(),

      label: formData.label.trim(),

      description: formData.description?.trim(),

      placeholder: formData.placeholder?.trim(),

      type: formData.type,

      step: formData.step?.trim(),

      group: formData.group?.trim(),

      width: formData.width,

      required: formData.required,

      is_readonly: formData.is_readonly,

      is_active: formData.is_active,

      is_system: formData.is_system,

      order: Number(formData.order) || 0,

      options: needsOptions ? formData.options : [],

      visible_for: formData.visible_for,

      editable_by: formData.editable_by,

      excel_column: formData.excel_column?.trim()?.toUpperCase(),

      excel_header: formData.excel_header?.trim(),

      validation: {
        ...formData.validation,
      },
    };
  };

  /**
   * =====================================================
   * SUBMIT
   * =====================================================
   */

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    const payload = buildPayload();

    try {
      if (isEditMode) {
        await dispatch(
          updateFieldConfig({
            id: initialData._id,
            data: payload,
          }),
        ).unwrap();
      } else {
        await dispatch(createFieldConfig(payload)).unwrap();
      }

      onSuccess?.();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <form className="field-config-form" onSubmit={handleSubmit}>
      {error && <div className="field-config-form__error">{error}</div>}

      <div className="field-config-form__grid">
        <div className="field-config-form__group">
          <label>Key *</label>

          <input
            name="key"
            value={formData.key}
            onChange={handleChange}
            onBlur={normalizeKey}
            disabled={isEditMode || isSubmitting}
            placeholder="vdab_nummer"
            className={validationErrors.key ? "input-error" : ""}
          />

          {validationErrors.key && (
            <span className="field-config-form__validation-error">
              {validationErrors.key}
            </span>
          )}
        </div>

        <div className="field-config-form__group">
          <label>Label *</label>

          <input
            name="label"
            value={formData.label}
            onChange={handleChange}
            disabled={isSubmitting}
            placeholder="VDAB nummer"
          />
        </div>

        <div className="field-config-form__group">
          <label>Type *</label>

          <select
            name="type"
            value={formData.type}
            onChange={handleTypeChange}
            disabled={isSubmitting}
          >
            {FIELD_TYPES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field-config-form__group">
          <label>Workflow stap *</label>

          <select name="step" value={formData.step} onChange={handleChange}>
            {STEP_OPTIONS.map((step) => (
              <option key={step.value} value={step.value}>
                {step.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field-config-form__group">
          <label>Groep</label>

          <input
            name="group"
            value={formData.group}
            onChange={handleChange}
            placeholder="persoonlijk"
          />
        </div>

        <div className="field-config-form__group">
          <label>Volgorde</label>

          <input
            type="number"
            name="order"
            value={formData.order}
            onChange={handleChange}
          />
        </div>

        <div className="field-config-form__group field-config-form__group--full">
          <label>Beschrijving</label>

          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Beschrijving voor beheerders"
          />
        </div>

        <div className="field-config-form__group">
          <label>Placeholder</label>

          <input
            name="placeholder"
            value={formData.placeholder}
            onChange={handleChange}
            placeholder="Typ hier..."
          />
        </div>

        <div className="field-config-form__group">
          <label>Breedte</label>

          <select name="width" value={formData.width} onChange={handleChange}>
            {WIDTH_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field-config-form__group">
          <label>Excel kolom</label>

          <input
            name="excel_column"
            value={formData.excel_column}
            onChange={handleChange}
            placeholder="G"
          />
        </div>

        <div className="field-config-form__group">
          <label>Excel titel</label>

          <input
            name="excel_header"
            value={formData.excel_header}
            onChange={handleChange}
            placeholder="VDAB nummer"
          />
        </div>

        {supportsMinMax && (
          <>
            <div className="field-config-form__group">
              <label>Minimum waarde</label>

              <input
                type="number"
                name="min"
                value={formData.validation.min}
                onChange={handleValidationChange}
              />
            </div>

            <div className="field-config-form__group">
              <label>Maximum waarde</label>

              <input
                type="number"
                name="max"
                value={formData.validation.max}
                onChange={handleValidationChange}
              />
            </div>
          </>
        )}

        {supportsTextValidation && (
          <>
            <div className="field-config-form__group">
              <label>Min lengte</label>

              <input
                type="number"
                name="min_length"
                value={formData.validation.min_length}
                onChange={handleValidationChange}
              />
            </div>

            <div className="field-config-form__group">
              <label>Max lengte</label>

              <input
                type="number"
                name="max_length"
                value={formData.validation.max_length}
                onChange={handleValidationChange}
              />
            </div>

            <div className="field-config-form__group field-config-form__group--full">
              <label>Regex validatie</label>

              <input
                name="regex"
                value={formData.validation.regex}
                onChange={handleValidationChange}
                placeholder="^[0-9]+$"
              />
            </div>
          </>
        )}
      </div>

      <div className="field-config-form__roles">
        <div className="field-config-form__role-section">
          <h3>Zichtbaar voor</h3>

          <div className="field-config-form__role-grid">
            {ROLE_OPTIONS.map((role) => (
              <label key={role.value}>
                <input
                  type="checkbox"
                  checked={formData.visible_for.includes(role.value)}
                  onChange={() => toggleRole("visible_for", role.value)}
                />

                {role.label}
              </label>
            ))}
          </div>
        </div>

        <div className="field-config-form__role-section">
          <h3>Bewerkbaar voor</h3>

          <div className="field-config-form__role-grid">
            {ROLE_OPTIONS.map((role) => (
              <label key={role.value}>
                <input
                  type="checkbox"
                  checked={formData.editable_by.includes(role.value)}
                  onChange={() => toggleRole("editable_by", role.value)}
                />

                {role.label}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="field-config-form__checks">
        <label>
          <input
            type="checkbox"
            name="required"
            checked={formData.required}
            onChange={handleChange}
          />
          Verplicht
        </label>

        <label>
          <input
            type="checkbox"
            name="is_readonly"
            checked={formData.is_readonly}
            onChange={handleChange}
          />
          Alleen lezen
        </label>

        <label>
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
          />
          Actief
        </label>

        <label>
          <input
            type="checkbox"
            name="is_system"
            checked={formData.is_system}
            onChange={handleChange}
          />
          Systeemveld
        </label>
      </div>

      {needsOptions && (
        <div className="field-config-form__options">
          <div className="field-config-form__options-header">
            <div>
              <h3>Opties</h3>

              <p>Label = zichtbaar, Value = opgeslagen waarde</p>
            </div>

            <button type="button" onClick={addOption}>
              Optie toevoegen
            </button>
          </div>

          <div className="field-config-form__option-list">
            {formData.options.map((option, index) => (
              <div key={index} className="field-config-form__option-row">
                <input
                  value={option.label}
                  onChange={(e) =>
                    handleOptionChange(index, "label", e.target.value)
                  }
                  placeholder="Label"
                />

                <input
                  value={option.value}
                  onChange={(e) =>
                    handleOptionChange(index, "value", e.target.value)
                  }
                  placeholder="Value"
                />

                <button type="button" onClick={() => removeOption(index)}>
                  Verwijder
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="field-config-form__actions">
        <button
          type="button"
          className="field-config-form__cancel"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Annuleren
        </button>

        <button
          type="submit"
          className="field-config-form__submit"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? "Opslaan..."
            : isEditMode
              ? "Wijzigingen opslaan"
              : "Veld aanmaken"}
        </button>
      </div>
    </form>
  );
}
